import React, { createContext, useContext, useEffect, useState } from "react";
import { translations } from "../i18n/translations";
import productsData from "../data/products.json";

const AppContext = createContext(null);

const readLocal = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export const AppProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => localStorage.getItem("luxera_lang") || "en");
  const [currency, setCurrency] = useState(() => localStorage.getItem("luxera_currency") || "EUR");
  const [cart, setCart] = useState(() => readLocal("luxera_cart", []));
  const [wishlist, setWishlist] = useState(() => readLocal("luxera_wishlist", []));
  const [products] = useState(productsData);
  const [cartOpen, setCartOpen] = useState(false);

  const t = translations[language];
  const dir = t.dir;

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = dir;
    localStorage.setItem("luxera_lang", language);
  }, [language, dir]);

  useEffect(() => {
    localStorage.setItem("luxera_currency", currency);
  }, [currency]);

  const syncCart = (items) => {
    setCart(items);
    localStorage.setItem("luxera_cart", JSON.stringify(items));
  };

  const syncWishlist = (ids) => {
    setWishlist(ids);
    localStorage.setItem("luxera_wishlist", JSON.stringify(ids));
  };

  const addToCart = (product_id, quantity = 1) => {
    const existing = cart.find((i) => i.product_id === product_id);
    let next;
    if (existing) {
      next = cart.map((i) => i.product_id === product_id ? { ...i, quantity: i.quantity + quantity } : i);
    } else {
      next = [...cart, { product_id, quantity }];
    }
    syncCart(next);
    setCartOpen(true);
  };

  const updateQty = (product_id, quantity) => {
    if (quantity <= 0) return removeFromCart(product_id);
    syncCart(cart.map((i) => i.product_id === product_id ? { ...i, quantity } : i));
  };

  const removeFromCart = (product_id) => {
    syncCart(cart.filter((i) => i.product_id !== product_id));
  };

  const toggleWishlist = (product_id) => {
    if (wishlist.includes(product_id)) syncWishlist(wishlist.filter((id) => id !== product_id));
    else syncWishlist([...wishlist, product_id]);
  };

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const cartSubtotal = cart.reduce((s, i) => {
    const p = products.find((p) => p.id === i.product_id);
    return s + (p ? p.price * i.quantity : 0);
  }, 0);

  return (
    <AppContext.Provider value={{
      language, setLanguage, currency, setCurrency, t, dir,
      products, cart, wishlist,
      addToCart, updateQty, removeFromCart, toggleWishlist,
      cartCount, cartSubtotal, cartOpen, setCartOpen,
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => useContext(AppContext);
